/**
 * Circuit breaker for consecutive auth/permission failures.
 *
 * Every 403 increments a counter. Once the counter hits the threshold,
 * a full diagnostic is captured, tokens are cleared, and auth:logout fires.
 * Any 2xx response resets the counter so transient permission issues don't
 * accumulate forever.
 *
 * A rolling buffer of the last N auth failures is kept in localStorage so
 * the user (or support) can inspect exactly what went wrong after being
 * logged out. Access it via window.__getAuthErrorLog() in the console.
 */

const STORAGE_KEY = 'auth_error_log';
const MAX_LOG_ENTRIES = 25;
const THRESHOLD = 3;

/**
 * Resolves the telemetry endpoint from the same env var httpClient uses.
 * No hard-coded localhost fallback — if the var isn't set we'll just skip
 * the server POST (localStorage log still records the entry locally).
 */
const getTelemetryEndpoint = (): string | null => {
  const base = process.env.REACT_APP_API_BASE_URL;
  if (!base) return null;
  return base.replace(/\/$/, '') + '/telemetry/auth-failure';
};

export interface AuthFailureEntry {
  timestamp: string;         // ISO string
  method: string;
  url: string;
  status: number;
  serverMessage?: string;
  userId?: string;
  userEmail?: string;
  userRole?: number | string;
  currentPath: string;
  userAgent: string;
  consecutiveCount: number;
}

let consecutive403s = 0;

const safeParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
};

const readLog = (): AuthFailureEntry[] =>
  safeParse<AuthFailureEntry[]>(localStorage.getItem(STORAGE_KEY), []);

const writeLog = (entries: AuthFailureEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_LOG_ENTRIES)));
  } catch (err) {
    // localStorage might be full or disabled — don't let logging fail the app
    console.warn('authCircuitBreaker: could not persist log entry', err);
  }
};

/**
 * Fire-and-forget POST of a log entry to the backend. Uses plain fetch (not
 * httpClient) so it bypasses our own auth interceptor — no recursion risk,
 * and it still works after tokens have been cleared. `sendBeacon` is used
 * when available so the request survives navigations / page unloads.
 */
const sendToServer = (entry: AuthFailureEntry, reason: string) => {
  const endpoint = getTelemetryEndpoint();
  if (!endpoint) return; // env not configured — rely on local log only

  const payload = {
    reason,
    method: entry.method,
    url: entry.url,
    status: entry.status,
    serverMessage: entry.serverMessage,
    userId: entry.userId,
    userEmail: entry.userEmail,
    userRole: entry.userRole != null ? String(entry.userRole) : undefined,
    currentPath: entry.currentPath,
    userAgent: entry.userAgent,
    consecutiveCount: entry.consecutiveCount,
    extra: { clientTimestamp: entry.timestamp },
  };

  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow — telemetry must never break the app */ });
  } catch {
    // Never let telemetry raise.
  }
};

const readCurrentUser = (): { id?: string; email?: string; role?: number | string } => {
  // Zustand persists useAuthStore to localStorage under 'auth-storage' by default.
  const raw = localStorage.getItem('auth-storage');
  if (!raw) return {};
  const parsed = safeParse<any>(raw, null);
  const u = parsed?.state?.user;
  if (!u) return {};
  return { id: u.id, email: u.email, role: u.role ?? u.role_id };
};

/** Called on every successful API response. */
export const recordAuthSuccess = () => {
  consecutive403s = 0;
};

/**
 * Called when an API returns 403.
 * Returns true when the circuit has tripped and the caller should treat
 * this as a terminal logout (token clear + auth:logout event already fired).
 */
export const record403 = (params: {
  method?: string;
  url?: string;
  status: number;
  serverMessage?: string;
}): { tripped: boolean; count: number } => {
  consecutive403s += 1;

  const user = readCurrentUser();
  const entry: AuthFailureEntry = {
    timestamp: new Date().toISOString(),
    method: (params.method || 'GET').toUpperCase(),
    url: params.url || '(unknown)',
    status: params.status,
    serverMessage: params.serverMessage,
    userId: user.id,
    userEmail: user.email,
    userRole: user.role,
    currentPath: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
    consecutiveCount: consecutive403s,
  };

  writeLog([...readLog(), entry]);
  sendToServer(entry, 'auth_403');
  console.warn(
    `[auth] 403 #${consecutive403s}/${THRESHOLD} — ${entry.method} ${entry.url}` +
    (entry.userRole !== undefined ? ` (role=${entry.userRole})` : '')
  );

  if (consecutive403s >= THRESHOLD) {
    sendToServer(entry, 'permission_circuit_breaker');
    console.error(
      `[auth] Circuit breaker tripped after ${consecutive403s} consecutive 403s. ` +
      `Forcing logout. Diagnostic log: window.__getAuthErrorLog()`
    );

    // Clear stored credentials before anyone can retry
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');

    // Tell the app to navigate to /login and clear state
    window.dispatchEvent(
      new CustomEvent('auth:logout', {
        detail: {
          reason: 'permission_circuit_breaker',
          consecutive: consecutive403s,
          lastEntry: entry,
        },
      })
    );

    consecutive403s = 0; // reset for next login
    return { tripped: true, count: entry.consecutiveCount };
  }

  return { tripped: false, count: entry.consecutiveCount };
};

export const getAuthErrorLog = (): AuthFailureEntry[] => readLog();
export const clearAuthErrorLog = (): void => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

// Expose the log on window for quick console access after an auto-logout.
if (typeof window !== 'undefined') {
  (window as any).__getAuthErrorLog = getAuthErrorLog;
  (window as any).__clearAuthErrorLog = clearAuthErrorLog;
}
